import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import type { RoadmapNode } from "../../api/roadmap-service";

import { childKey, pickAt } from "../../utils/roadmap-tree";
import { RoadmapChoiceBox } from "./RoadmapChoiceBox";
import { RoadmapNodeBox } from "./RoadmapNodeBox";

/** Horizontal room for the connector between a node and its children. */
const GAP = 24;

const StyledBranch = styled(Box)({
    display: 'flex',
    alignItems: 'center',
});

/** The column of children; its `::before` is the line leaving the parent. */
const StyledChildren = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: GAP,
    '&::before': {
        content: "''",
        position: 'absolute',
        left: 0,
        top: '50%',
        width: GAP,
        borderTop: `1px solid ${ theme.palette.divider }`,
    },
}));

/** One child; `::before` is its stub from the spine, `::after` the part of the spine it spans. */
const StyledChild = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(0.5, 0, 0.5, `${ GAP }px`),
    '&::before': {
        content: "''",
        position: 'absolute',
        left: 0,
        top: '50%',
        width: GAP,
        borderTop: `1px solid ${ theme.palette.divider }`,
    },
    '&::after': {
        content: "''",
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        borderLeft: `1px solid ${ theme.palette.divider }`,
    },
    '&:first-of-type::after': { top: '50%' },
    '&:last-of-type::after': { bottom: '50%' },
    '&:only-of-type::after': { display: 'none' },
}));

/** Alternatives of a choice that expects only some of them hang on dashed lines, like a planned node is dashed. */
const ALTERNATIVE_LINES = {
    '&::before': { borderTopStyle: 'dashed' },
    '&::after': { borderLeftStyle: 'dashed' },
};

export type RoadmapBranchProps = {
    node: RoadmapNode;
    nodeKey: string;
    /** Distance from the roadmap itself, which is 0. */
    depth: number;
    /** The selected level, which decides how many alternatives of a choice are expected. */
    level: number;
    collapsed: Set<string>;
    levelName: (level: number) => string;
    onToggle: (nodeKey: string) => void;
    onOpen: (node: RoadmapNode) => void;
    onNotes: (node: RoadmapNode) => void;
};

/** A node with its subtree to the right of it, drawn as a left-to-right mindmap. */
export function RoadmapBranch(props: RoadmapBranchProps) {
    const { node, nodeKey, depth, level, collapsed, levelName, onToggle, onOpen, onNotes } = props;
    const isCollapsed = collapsed.has(nodeKey);
    const canOpen = !!node.courseId;
    const isChoice = node.kind === 'CHOICE';
    const count = isChoice ? pickAt(node, level) : 0;
    const isAlternative = isChoice && count < node.children.length;

    return <StyledBranch>
        { isChoice ? (
            <RoadmapChoiceBox node={ node }
                              count={ count }
                              levelName={ levelName }
                              collapsed={ isCollapsed }
                              onToggle={ () => onToggle(nodeKey) }/>
        ) : (
            <RoadmapNodeBox node={ node }
                            depth={ depth }
                            levelName={ levelName(node.level) }
                            collapsed={ isCollapsed }
                            onToggle={ () => onToggle(nodeKey) }
                            onOpen={ canOpen ? () => onOpen(node) : undefined }
                            onNotes={ node.notes.length > 0 ? () => onNotes(node) : undefined }/>
        ) }

        { !isCollapsed && node.children.length > 0 && (
            <StyledChildren>
                { node.children.map((child, index) => (
                    <StyledChild key={ childKey(nodeKey, index) } sx={ isAlternative ? ALTERNATIVE_LINES : undefined }>
                        <RoadmapBranch { ...props }
                                       node={ child }
                                       nodeKey={ childKey(nodeKey, index) }
                                       depth={ depth + 1 }/>
                    </StyledChild>
                )) }
            </StyledChildren>
        ) }
    </StyledBranch>;
}
